import { Suspense, useEffect, useRef, useState } from "react";
import StandortModal from "../../Components/StandortModal/StandortModal";
import TextBauSteine from "../../Components/TextbauSteine";
import DividerCard from "../../Components/Ui/Card/DividerCard";
import CustomButton from "../../Components/Ui/CustomButton";
import ToggleButton from "../../Components/Ui/ToggleButton/ToggleButton";
import { Address } from "../../Store/interfaces";
import { useScheinContext } from "../../Store/schein-context";
import { useUIContext } from "../../Store/ui-context";
import {
  getCalculatedDistance,
  getLatLongFromAddress,
  getStoredAddresses,
} from "../../Utils/api";

const Info: React.FC<{ onButtonClick: any }> = ({ onButtonClick }) => {
  const {
    day,
    startTime,
    fahrtBerechnen,
    setFahrtBerechnen,
    endTime,
    setDay,
    setStartTime,
    setEndTime,
    startOrt,
    setStartOrt,
    zielOrt,
    comment,
    setComment,
    distanz,
    setDistanz,
    fahrtZeit,
    setFahrtZeit,
  } = useScheinContext();

  const { setShowTextbauSteine, showTextbauSteine } = useUIContext();
  const [showStartOrt, setShowStartOrt] = useState(false);
  const [halbeFahrt, setHalbeFahrt] = useState(false);

  useEffect(() => {
    if (!isValidDate(day)) {
      setDay(new Date(getCurrentDate()));
    }

    if (!isValidDate(startTime)) {
      const currentDate = new Date();

      // Add 2 hours to the current date
      const startDate = new Date(currentDate.getTime() - 1 * 60 * 60 * 1000);
      setStartTime(startDate);
    }

    if (!isValidDate(endTime)) {
      const currentDate = new Date();

      // Add 2 hours to the current date
      const endDate = new Date(currentDate.getTime() + 2 * 60 * 60 * 1000);
      setEndTime(endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrentDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, "0"); // Month is 0-indexed
    const day = today.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const isValidDate = (day: Date | string) => {
    let date;

    if (typeof day === "string") {
      date = new Date(day);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return false;
      }
    } else if (day instanceof Date) {
      date = day;
    } else {
      return false;
    }

    const currentDate = new Date();
    // Normalize dates to remove time for accurate comparison
    const normalizedDay = new Date(date.toDateString());
    const normalizedCurrentDate = new Date(currentDate.toDateString());

    // Check if 'date' is today or a future date
    return normalizedDay >= normalizedCurrentDate;
  };

  const dateChangeHandler = (changedDay: string) => {
    setDay(new Date(changedDay));
  };

  const commentChangeHandler = (value: string) => {
    setComment(value);
  };

  function textbauSteinSelected(t: string) {
    let newComment = comment;
    if (comment.length > 0) {
      newComment = comment.trimEnd() + " " + t;
    } else {
      newComment = t;
    }
    setComment(newComment);
  }

  let content = null;

  if (showTextbauSteine) {
    content = <TextBauSteine textSelected={textbauSteinSelected} />;
  }

  if (showStartOrt) {
    content = (
      <StandortModal
        isVisible={true}
        onClose={() => {
          setShowStartOrt(false);
        }}
      />
    );
  }

  // ################################################################################################################################################################

  // ---------------------------------------------------------
  let longitudeC: number = 0;
  let latitudeC: number = 0;

  let gpsLocationC = { latitudeC, longitudeC };

  let longitudeT: number = 0;
  let latitudeT: number = 0;

  let gpsLocationT = { latitudeT, longitudeT };
  // ---------------------------------------------------------

  // Guards against out-of-order async responses: only the most recently
  // started "anfahrtBerechnen" call is allowed to write its result to state.
  const requestIdRef = useRef(0);

  const settingDefaultStartort = async () => {
    if (!startOrt) {
      const data = await getStoredAddresses();

      data.forEach((ad: Address) => {
        if (ad.is_default) {
          setStartOrt(ad.adresse);
        }
      });
    }
  };

  useEffect(() => {
    setFahrtBerechnen(true);
    settingDefaultStartort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDistanceToHomeBaseRequest = async (requestId: number) => {
    const data = await getCalculatedDistance(
      gpsLocationT.latitudeT,
      gpsLocationC.latitudeC,
      gpsLocationT.longitudeT,
      gpsLocationC.longitudeC
    );

    if (!data) return;

    // returns the distance in meter
    // let distance = data.distance / 1000;
    let distance = (data.distance / 1000) * 2;
    // returns the distance in km now
    // let travelTime = data.time / 60000;
    let travelTime = (data.time / 60000) * 2;

    // returns traveltime in min now
    if (halbeFahrt) {
      distance = distance / 2;
      travelTime = travelTime / 2;
    }

    // a newer anfahrtBerechnen call has started in the meantime -> discard this result
    if (requestId !== requestIdRef.current) return;

    setDistanz(Number(distance.toFixed(2)));
    setFahrtZeit(Number(travelTime.toFixed(2)));
    return distance;
  };

  // aquires the geografical data for the given address
  const getLatLong = async (address: string, isTechnician: boolean = false) => {
    if (address === "" || address.length < 1) {
      address = localStorage.getItem("Standardadresse") || "";
    }

    const data = await getLatLongFromAddress(address);

    const lat = data.lat;
    const long = data.lng;

    if (!isTechnician) {
      gpsLocationC.latitudeC = lat;
      gpsLocationC.longitudeC = long;
    } else {
      gpsLocationT.latitudeT = lat;
      gpsLocationT.longitudeT = long;
    }
  };

  // ################################################################################################################################################################

  const anfahrtBerechnen = async () => {
    const requestId = ++requestIdRef.current;
    try {
      // check if startOrt is the default
      await getLatLong(startOrt, true); // startOrt
      await getLatLong(zielOrt);
      await getDistanceToHomeBaseRequest(requestId);
    } catch (error) {
      console.log("Error in Info Anfahrtsberechnung: " + error);
    }
  };

  useEffect(() => {
    anfahrtBerechnen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOrt, zielOrt, halbeFahrt]);

  // ################################################################################################################################################################

  const handleStartTimeChange = (event: any) => {
    setStartTime(convertStringToDate(event.target.value));
  };

  const handleEndTimeChange = (event: any) => {
    setEndTime(convertStringToDate(event.target.value));
  };

  const convertStringToDate = (timeString: string): Date => {
    const [hours, minutes] = timeString.split(":").map(Number);
    const newDate = new Date();
    newDate.setHours(hours);
    newDate.setMinutes(minutes);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
  };

  // ################################################################################################################################################################

  return (
    <>
      {content ? (
        content
      ) : (
        <main className="w-full mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center m-5">
            <DividerCard className="my-5" heading="Arbeitszeit">
              <div className="w-auto flex flex-col items-center justify-center">
                <label
                  htmlFor="day"
                  className="font-sans font-bold text-sm sm:text-base"
                >
                  Datum
                </label>
                <input
                  className="w-auto p-2 border border-gray-400 rounded focus:ring-blue-500 focus:border-blue-500"
                  type="date"
                  id="day"
                  value={day.toISOString().substr(0, 10)}
                  onChange={(e) => dateChangeHandler(e.target.value)}
                />
              </div>

              <div className="w-auto flex flex-col items-center justify-center">
                <label
                  htmlFor="starttime"
                  className="text-sm font-bold sm:text-base"
                >
                  Start
                </label>
                <input
                  type="time"
                  className="w-auto p-2 border border-gray-400 rounded focus:ring-blue-500 focus:border-blue-500"
                  value={startTime.toTimeString().substr(0, 5)}
                  onChange={(e) => handleStartTimeChange(e)}
                />
              </div>

              <div className="w-auto flex flex-col items-center justify-center">
                <label
                  htmlFor="endtime"
                  className="text-sm font-bold sm:text-base"
                >
                  Ende
                </label>
                <input
                  type="time"
                  className="w-auto p-2 border border-gray-400 rounded focus:ring-blue-500 focus:border-blue-500"
                  value={endTime.toTimeString().substr(0, 5)}
                  onChange={(e) => handleEndTimeChange(e)}
                />
              </div>
            </DividerCard>
            <DividerCard className="my-5" heading="Arbeitsort">
              {/* Toggle Buttons jetzt untereinander auf kleinen Screens, nebeneinander auf großen Screens */}
              <div className="flex items-center sm:justify-center">
                <span className="block text-gray-700 text-sm sm:text-base mr-5">
                  Anfahrt berechnen
                </span>
                <ToggleButton
                  className="ml-2"
                  isChecked={fahrtBerechnen}
                  onToggle={() => setFahrtBerechnen(!fahrtBerechnen)}
                />
              </div>

              <div className="flex items-center sm:justify-center">
                <span className="block text-gray-700 text-sm sm:text-base mr-5">
                  Halbe Anfahrt
                </span>
                <ToggleButton
                  className="ml-2"
                  isChecked={halbeFahrt}
                  onToggle={() => setHalbeFahrt(!halbeFahrt)}
                />
              </div>

              <div className="mx-auto">
                <Suspense>
                  {fahrtBerechnen ? (
                    <label className="text-black text-center w-[200px] mx-auto">
                      An- & Abfahrt zum Kunden: {distanz} km{" "}
                      {fahrtBerechnen && distanz > 100 && fahrtZeit > 60 && (
                        <span className="block text-red mt-1">
                          !An- & Abfahrt über 100 km und Fahrtzeit über 1
                          Stunde, bitte Pauschale verwenden!
                        </span>
                      )}
                    </label>
                  ) : (
                    <div>
                      <label>Keine Anfahrt berechnet!</label>
                    </div>
                  )}
                </Suspense>
              </div>
            </DividerCard>

            {/* </div> */}

            <div className="w-full max-w-7xl">
              <CustomButton
                className="w-full rounded-lg"
                theme="navy"
                onClick={() => setShowStartOrt(true)}
              >
                Startort festlegen
              </CustomButton>
            </div>

            <div className="w-full max-w-7xl mt-5">
              <h2 className="text-blue_royal text-lg sm:text-xl md:text-2xl font-bold text-center">
                Grund des Technikereinsatzes
              </h2>

              <div className="flex justify-end mb-3">
                <CustomButton
                  className="w-full my-4"
                  theme="navy"
                  onClick={() => setShowTextbauSteine(true)}
                >
                  Textbausteine
                </CustomButton>
              </div>

              <textarea
                className="w-full h-32 p-3 border border-gray-400 bg-gray-100 text-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Kommentar hier..."
                value={comment}
                onChange={(e) => commentChangeHandler(e.target.value)}
              />
            </div>

            <div className="w-full max-w-7xl grid grid-cols-2 gap-4 mt-10">
              <CustomButton
                className="w-full"
                theme="gray"
                onClick={() => onButtonClick("kundensuche")}
              >
                Zurück
              </CustomButton>

              <CustomButton
                className="w-full"
                theme="navy"
                onClick={() => onButtonClick("leistung")}
              >
                Weiter
              </CustomButton>
            </div>
          </div>
        </main>
      )}
    </>
  );
};

export default Info;
